/**
 * SafetyStripe — always-visible trust indicator in the chat header.
 *
 * Renders a 3-px colored bar + subtle label that derives the peer's
 * current fingerprint trust state without the user opening any dialog.
 *
 * Colors:
 *   emerald — verified (current fp matches our ledger)
 *   blue    — acknowledged rotation (new fp, but user already ack'd)
 *   slate   — first-seen (never trusted this peer)
 *   red     — suspicious (fp changed silently, not acknowledged)
 *
 * Click → opens the KeyTransparencyDialog.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEDGER_PREFIX = 'ghostcomm_seen_fingerprints_';

function loadLedger(userId) {
  try {
    const raw = window.localStorage.getItem(LEDGER_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const SafetyStripe = ({ peerId, peerUsername, onClick }) => {
  const [state, setState] = useState('loading');
  const [currentFp, setCurrentFp] = useState(null);

  useEffect(() => {
    if (!peerId) return;
    // Duress: decoy peers don't exist server-side and a 404 on
    // /users/{id}/key-history is a network reveal. Short-circuit
    // to a neutral "verified" visual so the coercer sees a normal
    // trust badge with no server round-trip.
    if (typeof window !== 'undefined' &&
        sessionStorage.getItem('gs_duress_mode') === '1') {
      setState('verified');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/${peerId}/key-history`);
        const current = res.data?.entries?.find((e) => e.is_current);
        if (!current) {
          if (!cancelled) setState('no-key');
          return;
        }
        const fp = current.fingerprint;
        if (!cancelled) setCurrentFp(fp);
        const ledger = loadLedger(peerId);
        if (!ledger) {
          if (!cancelled) setState('first-seen');
          return;
        }
        if (ledger.fingerprint === fp) {
          if (!cancelled) setState('verified');
          return;
        }
        if ((ledger.acknowledged_fingerprints || []).includes(fp)) {
          if (!cancelled) setState('acknowledged');
          return;
        }
        if (!cancelled) setState('suspicious');
      } catch (e) {
        if (!cancelled) setState('error');
      }
    })();
    // Re-derive whenever the ledger changes (dialog acknowledgement
    // writes to localStorage). Listen for storage events across tabs
    // AND trigger manually via `onFocus` so in-tab writes refresh too.
    const onStorage = (ev) => {
      if (ev.key && ev.key.startsWith(LEDGER_PREFIX + peerId)) {
        // Re-evaluate with fresh ledger
        const ledger = loadLedger(peerId);
        if (cancelled || !currentFp) return;
        if (!ledger) setState('first-seen');
        else if (ledger.fingerprint === currentFp) setState('verified');
        else if ((ledger.acknowledged_fingerprints || []).includes(currentFp))
          setState('acknowledged');
        else setState('suspicious');
      }
    };
    window.addEventListener('storage', onStorage);
    // Quick re-check every 10s — cheap, catches in-tab ledger writes
    // from the KeyTransparencyDialog (no storage event fires in same tab).
    const iv = window.setInterval(() => {
      if (cancelled || !currentFp) return;
      const ledger = loadLedger(peerId);
      if (!ledger) setState((s) => (s === 'first-seen' ? s : 'first-seen'));
      else if (ledger.fingerprint === currentFp)
        setState((s) => (s === 'verified' ? s : 'verified'));
      else if ((ledger.acknowledged_fingerprints || []).includes(currentFp))
        setState((s) => (s === 'acknowledged' ? s : 'acknowledged'));
      else setState((s) => (s === 'suspicious' ? s : 'suspicious'));
    }, 10000);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', onStorage);
      window.clearInterval(iv);
    };
  }, [peerId, currentFp]);

  // Style + label per state. Keep colors muted — the stripe shouldn't
  // dominate the header, just be legible at a glance.
  const config = {
    loading: { stripe: 'bg-slate-700', text: 'text-slate-500', Icon: null, label: '' },
    error: { stripe: 'bg-slate-700', text: 'text-slate-500', Icon: null, label: '' },
    'no-key': {
      stripe: 'bg-slate-700',
      text: 'text-slate-500',
      Icon: ShieldQuestion,
      label: 'No key yet',
    },
    'first-seen': {
      stripe: 'bg-slate-500',
      text: 'text-slate-400',
      Icon: ShieldQuestion,
      label: 'Untrusted — click to verify',
    },
    verified: {
      stripe: 'bg-emerald-500',
      text: 'text-emerald-400',
      Icon: ShieldCheck,
      label: 'Verified',
    },
    acknowledged: {
      stripe: 'bg-sky-500',
      text: 'text-sky-400',
      Icon: ShieldCheck,
      label: 'Rotation acknowledged',
    },
    suspicious: {
      stripe: 'bg-red-500',
      text: 'text-red-400',
      Icon: ShieldAlert,
      label: 'Key changed silently',
    },
  }[state];

  if (state === 'loading' || state === 'error') {
    // Thin, near-invisible resting state — avoids flashing colored
    // stripes during the initial fetch.
    return <div className="h-[3px] w-full mt-1 rounded-full bg-slate-800/60" />;
  }

  const { stripe, text, Icon, label } = config;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full text-left mt-1 flex flex-col gap-0.5 focus:outline-none"
      data-testid="safety-stripe"
      data-safety-state={state}
      title={
        peerUsername
          ? `Key transparency — ${peerUsername}`
          : 'Key transparency'
      }
    >
      <div className={`h-[3px] w-full rounded-full ${stripe} transition-colors`} />
      <div className={`text-[11px] flex items-center gap-1 ${text} opacity-80 group-hover:opacity-100 transition-opacity`}>
        {Icon && <Icon className="w-3 h-3" />}
        <span>{label}</span>
      </div>
    </button>
  );
};

export default SafetyStripe;
